import axios from 'axios';
import { api } from './api';
import type { AttributionPayload } from '../lib/landing/attribution';
import type { StoredAnswer, Via } from '../components/regularizacion/questions';

/**
 * Comprueba si un error de axios corresponde a un 409 Conflict.
 * Útil para tratar duplicados de contacto como éxito implícito en el flujo
 * de captación de leads (el contacto ya está en CRM, el lead puede continuar).
 */
function isConflict(err: unknown): boolean {
  if (axios.isAxiosError(err)) return err.response?.status === 409;
  // Fallback para tests/errores plain con shape de axios
  if (typeof err === 'object' && err !== null) {
    const e = err as { isAxiosError?: boolean; response?: { status?: number } };
    return Boolean(e.isAxiosError) && e.response?.status === 409;
  }
  return false;
}

// Hereda los campos de atribución (utm_*, fbclid, fbp, fbc, meta_event_id, …)
// para que el quiz /leads/contacts pueda reenviar la procedencia de campaña.
// Antes este payload no los tenía y TODO lead del quiz quedaba como 'organic'.
export interface CreateLeadContactPayload extends AttributionPayload {
  name: string;
  phone: string;
  via: Via;
  eligible: boolean;
  lang: string;
  answers: Record<string, StoredAnswer>;
}

export interface TrackCompletionPayload {
  via: Via;
  eligible: boolean;
}

export const leadService = {
  async createContact(payload: CreateLeadContactPayload): Promise<void> {
    await api.post('/leads/contacts', payload);
  },

  async trackCompletion(payload: TrackCompletionPayload): Promise<void> {
    await api.post('/leads/stats', payload);
  },
};

// --- Landing lead service (for marketing landing pages from MigroLanding) ---

export interface CreateLandingLeadPayload {
  name: string;
  email: string;
  phone: string;
  interest?: string;
  landing_product?: string;
  source?: string;
  // ── UTMs (capturados de la URL de la landing) ──────────────────────────
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  /**
   * Atribución por ad/adset — sprint MAYO PILI AUTOMATICA.
   * Permite saber qué creative/adset generó cada lead para el real-CPL CRM
   * y la reasignación automática de budget en Job 4.
   */
  utm_content?: string;
  utm_term?: string;
  /** Macro Meta `{{campaign.id}}_{{adset.id}}_{{ad.id}}`; el backend lo parsea en IDs de objeto. */
  utm_id?: string;
  // ── Meta Pixel / CAPI ──────────────────────────────────────────────────
  fbclid?: string;
  fbp?: string;
  /** Cookie `_fbc` (formato `fb.1.<unix_ms>.<fbclid>`). Mejora el matching CAPI. */
  fbc?: string;
  /**
   * Event ID compartido con el pixel browser para deduplicación CAPI.
   * El backend CRM lo guarda y el job send_conversions lo reenvía a Meta CAPI
   * con el mismo event_id que ya recibió desde browser.
   */
  meta_event_id?: string;
  /** Atribución directa por URL params si Meta los pasa (`&ad_id=...&adset_id=...`). */
  ad_id?: string;
  adset_id?: string;
  campaign_id?: string;
  /** Plataforma origen inferida (`fb`, `ig`, `tiktok`, `youtube`...). */
  platform?: string;
  // ── Quiz de las landings dark (`/leads/dark/*`) ────────────────────────
  /**
   * Respuestas del quiz multi-step. El backend las persiste en
   * `crm_contacts.quiz_answers` (JSONB) para que el equipo de ventas
   * las consulte al llamar al lead.
   */
  quiz_answers?: Record<string, string>;
  /** Ruta de la landing donde se completó el quiz (ej. `/leads/dark/irregular`). */
  quiz_landing_path?: string;
  /**
   * URL completa donde el usuario submitió el form (`window.location.href`).
   * El backend lo reenvía a Meta CAPI como `event_source_url` top-level.
   * Meta lo exige en cuentas en categorías con restricciones (legal/inmigración).
   * Mejora EMQ ~0.5-1.0 vs el default `https://www.migro.es`.
   */
  event_source_url?: string;
  // ── Captación física Madrid (QR locutorios) ───────────────────────────────
  /**
   * Código del QR escaneado (`{comercial:3}{negocio:3}` → 001ABC). El backend
   * crea la atribución física (comercial + negocio) en `captacion_lead_atribucion`.
   */
  captacion_code?: string;
  /** ID del scan de `/captacion/resolve` para enlazar el lead con el escaneo. */
  captacion_scan_id?: string;
}

export const landingLeadService = {
  /**
   * Crea un contacto en CRM desde una landing pública.
   *
   * Idempotencia: si el backend responde 409 Conflict (el contacto ya existe
   * con el mismo email/teléfono), lo tratamos como éxito y no lanzamos. Esto
   * evita que el form se quede atascado cuando un usuario vuelve a la misma
   * landing y reenvía. Los logs de api.migro.es muestran patrones de
   * double-submit desde Instagram/Facebook in-app browsers donde el primer
   * POST es 201 y los siguientes 8+ son 409: el catch genérico bloqueaba el
   * avance al siguiente paso del funnel (TestInteractivo, LeadDarkLayout,
   * LandingNacionalidad, LeadReagrupacion).
   */
  async createContact(payload: CreateLandingLeadPayload): Promise<void> {
    // Auto-popula event_source_url con la URL actual si el caller no la pasa.
    // El backend lo reenvía a Meta CAPI top-level (exigido en cuentas con
    // restricciones: legal/inmigración). Hacerlo aquí evita tener que tocar
    // 9 call sites; quien quiera sobreescribirlo (p. ej. un quiz que ya
    // redirigió antes de submitar) lo pasa explícito en el payload.
    const enriched: CreateLandingLeadPayload =
      payload.event_source_url || typeof window === 'undefined'
        ? payload
        : { ...payload, event_source_url: window.location.href };

    try {
      // return_existing_on_duplicate=true: ante un duplicado, el backend devuelve
      // 200 con el contacto existente (no 409) y NO dispara la alerta de
      // re-engagement. Imprescindible para el double-submit de los in-app browsers
      // (IG/FB reintentan el POST 8+ veces): evita que cada reintento se trate como
      // un re-interés real y spamee a los agentes con "ha vuelto a interesarse".
      await api.post('/crm/contacts', enriched, {
        params: { return_existing_on_duplicate: true },
      });
    } catch (err) {
      // 409 Conflict = el contacto ya existe en CRM (mismo email/teléfono).
      // Lo tratamos como éxito implícito para que el funnel avance.
      if (isConflict(err)) return;
      throw err;
    }
  },
};
